/**
 * PLAN §F2 — the open request editor, which is the stale copy an MCP update
 * would otherwise be silently overwritten by.
 *
 * ==========================================================================
 * THIS MODULE IS THE ONE PLACE IN `src/mcp/` ALLOWED TO NAME `tabsStore`, AND
 * THAT IS A PINNED EXCEPTION WITH A COMPENSATING GUARD, NOT A RELAXATION.
 * ==========================================================================
 *
 * `mcpUiRefresh.test.js` and `mcpBoardUiRefresh.test.js` forbid every file
 * under `src/mcp/` from naming `tabsStore`, `navigateToPanel`,
 * `openDocumentTab`, `openTaskManagerBoardTab`, `closeTaskManagerBoardTab` or
 * `requestTaskFocus`. Those needles exist to stop this server DRIVING the
 * user's window: opening a tab, closing one, or yanking the app to another
 * panel while the user is working somewhere else. Every one of them still
 * holds, for this file too.
 *
 * What this file does is a different thing, and tranche 3 cannot ship without
 * it. `store/tabsStore.js` keeps `requestTabs`, each a FULL DENORMALIZED COPY
 * of a request, and `RequestBuilder` mirrors that copy into its own `useState`
 * fields. The two save payloads (`RequestBuilder.js:1751` and `:1770`) write
 * that whole copy back on Ctrl+S. So:
 *
 *     AN MCP UPDATE TO A REQUEST THE USER HAS OPEN IS REVERTED THE NEXT TIME
 *     THEY PRESS CTRL+S, UNLESS THE OPEN TAB IS PATCHED TOO.
 *
 * The app already built the fix and this reuses it rather than inventing a
 * second one. `requestTabSyncRevision` (`store/tabsStore.js:33/65/149`) is
 * bumped by every in-app writer — `useAiActions.js:470, 608, 705, 833` — and
 * consumed by `RequestBuilder`'s inbound-sync `useLayoutEffect` (`:615-658`):
 *
 *     const revisionBumped = syncRevision !== prevSyncRevisionRef.current;
 *     if (tabChanged || revisionBumped) { applyInitialDataToEditor(initialData); return; }
 *
 * BOTH HALVES ARE REQUIRED and doing only the bump is worse than doing
 * nothing: `initialData` comes from the tab record, so a bump with an unpatched
 * tab makes the editor re-apply its own stale copy. `mcpCollectionUpdates.test.js`
 * ablates the patch alone and the tab still holds the OLD name — a
 * bump-count assertion cannot see that.
 *
 * ## Why the bump is not merely an optimisation here
 *
 * That effect's fall-through diff (`RequestBuilder.js:650-657`) compares only
 * **body, url, method, headers**. `params`, `cookies`, `auth`,
 * `preRequestScript`, `testScript` and `formDataFields` are NOT in
 * `fieldsChanged`. For the app's own paths that is masked because every writer
 * bumps. For an MCP update that changes only `params` or only `auth`, the bump
 * is the ONLY mechanism that re-seeds the editor. (That omission is a
 * pre-existing bug and is chipped separately; nothing here fixes it.)
 *
 * ## What this file may and may not touch — the compensating guard
 *
 * It imports exactly two SETTERS and reads nothing:
 *
 *   * `setRequestTabs` is used with a FUNCTIONAL updater, so the tab array is
 *     never read out of the store by this module. That matters beyond style —
 *     `requestTabs[i].response` is the only headless copy of a real HTTP
 *     RESPONSE in the app, and a response body is the one thing this whole
 *     surface refuses to have a code path for (**§C**). This module never
 *     receives one, never names one, and `mcpAttackSurface.test.js` asserts it
 *     mentions neither `response` nor `getState` nor `useTabsStore`.
 *   * `setRequestTabSyncRevision`, likewise functional.
 *
 * Nothing here opens, closes, selects, focuses or reorders a tab. **O36 still
 * holds: report, never navigate.**
 *
 * ## Deletes deliberately do NOT touch tabs
 *
 * The app's own `deleteRequest` (`useCollections.js:519-521`) closes the tab of
 * a request it removes. This server does not, and the reason is that an
 * external agent closing an editor would discard whatever the user had typed
 * into it, with no warning and no undo, on a window they may not be looking
 * at. The cost of not closing it is a phantom tab — and it IS only a phantom:
 * `saveRequest`'s local branch resolves the request out of the collections tree
 * and its update arm is `.map(...)` over an array the record is no longer in
 * (`useCollections.js:1223-1233`), so a Ctrl+S on a deleted request changes
 * nothing and CANNOT resurrect it. Stated in `IMPLEMENTATION.md` and on the
 * hands-on checklist.
 */
import { setRequestTabs, setRequestTabSyncRevision } from '../store/tabsStore';

/**
 * The tab fields an MCP write may set, and no others. Built key by key from
 * the NEW stored record — never spread from it — for the same reason the read
 * projection is built key by key: a field added to the record model tomorrow
 * reaches the user's open editor only if somebody wrote a line for it.
 *
 * `preRequestScript` and `testScript` are deliberately absent. This server
 * never changes them (**O33**), so the tab keeps whatever the user has typed
 * there, including unsaved script edits.
 */
export function tabPatchFromRequest(request) {
  if (!request || typeof request !== 'object') return null;
  return {
    name: String(request.name || ''),
    method: String(request.method || 'GET').toUpperCase(),
    url: String(request.url || ''),
    headers: request.headers,
    params: request.params,
    cookies: request.cookies,
    body: request.body,
    bodyType: request.bodyType,
    rawBodyLanguage: request.rawBodyLanguage,
    formDataFields: request.formDataFields,
    auth: request.auth,
    description: typeof request.description === 'string' ? request.description : '',
    mock: request.mock,
    folderId: request.folder_id == null ? null : String(request.folder_id),
  };
}

/**
 * Patch the open tab for `requestId`, if one is open, and bump the revision.
 *
 * Returns `true` when a tab was actually patched. **Nothing happens when no
 * tab is open** — not the patch and not the bump — because a revision bump
 * makes every open request editor re-seed itself from its tab record, and
 * doing that for a request nobody has open is a visible flicker in the user's
 * window in exchange for nothing.
 *
 * The "was a tab open" question is answered INSIDE the functional updater
 * rather than by reading the store, which is what keeps the response copy out
 * of reach (see the header). zustand runs the updater synchronously, so the
 * flag is set by the time the next line reads it.
 */
export function patchOpenRequestTab(requestId, patch) {
  if (!requestId || !patch) return false;
  const wanted = String(requestId);
  let patched = false;
  setRequestTabs((prev) => {
    if (!Array.isArray(prev)) return prev;
    const next = prev.map((tab) => {
      if (String(tab?.originalId) !== wanted && String(tab?.id) !== wanted) return tab;
      patched = true;
      // `hasUnsavedChanges: false` matches what the app's own writers do
      // (`useAiActions.js:468`): the editor now agrees with what is stored, so
      // leaving the dirty flag on would prompt the user to save a copy they
      // did not make.
      return { ...tab, ...patch, hasUnsavedChanges: false };
    });
    return patched ? next : prev;
  });
  if (patched) setRequestTabSyncRevision((n) => (typeof n === 'number' ? n + 1 : 1));
  return patched;
}
